/**
 * The surfaces and press treatments shared by the chrome-level controls.
 *
 * Promoted from `/prototypes/_shared` with `Switcher`, the first component to
 * need them outside that surface. The prototype's own modules now compose
 * these rather than restating them, so the design has one definition.
 */

/**
 * The overlay surface — for anything that floats above a pane.
 *
 * NOT the card surface. In dark a card is `white/[0.055]` behind a blur, and a
 * popover using the same treatment is a translucent surface stacked on a
 * translucent surface: the two wash into each other and the text stops being
 * readable. That is the failure Apple names outright — never stack a light
 * material on another light one.
 *
 * So an overlay is opaque in both themes. It is also the smaller, higher
 * surface in the stack, and a smaller surface reading as *thicker* is what
 * makes the depth order legible: the card is the page's material, the popover
 * is a solid object resting on top of it.
 *
 * The shadow is deeper than a card's for the same reason — it has further to
 * fall.
 */
export const OVERLAY_SURFACE = [
  "rounded-nf-ds-overlay",
  "bg-nf-ds-surface-overlay",
  "shadow-nf-ds-overlay",
  "ring-1 ring-nf-ds-surface-overlay-ring ring-inset",
].join(" ");

/**
 * Press feedback: instant, on pointer-down, transform and colour only.
 * `ease-in` is never right here — it withholds movement during the moment the
 * user is watching hardest.
 *
 * `cursor-pointer` is part of the treatment because Tailwind v4's preflight
 * dropped the browser default on `<button>`: every hand-rolled control in the
 * app renders with an arrow unless it says otherwise, and the DS `Button`
 * already carries the class for exactly this reason. Putting it here means a
 * control that opts into the press cannot forget it. `disabled:pointer-events-none`
 * below keeps it off a disabled one.
 */
export const CONTROL_PRESS =
  // Fill, text colour and the press share the one curve and the one clock,
  // so a ghost control whose hover is only a text-colour change fades like a
  // filled one does rather than snapping while its neighbour eases.
  //
  // `scale` is listed on its own: Tailwind v4's `scale-*` writes the
  // standalone `scale` property, not `transform`, so a list naming only
  // `transform` leaves the press to snap. (`transition-transform` covers it;
  // an arbitrary list has to say it.)
  "cursor-pointer transition-[background-color,color,transform,scale] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] motion-reduce:transition-none motion-reduce:active:scale-100 disabled:pointer-events-none disabled:opacity-50";

/**
 * The resting field. A hairline rather than a fill: a control sitting inside a
 * pane has no fill contrast to separate it, so the ring is the only edge it
 * has — the same reason the card dropped its ring and the control kept one.
 */
export const FIELD_BASE =
  "w-full min-w-0 bg-nf-ds-surface-ground font-nf-ds-sans ring-1 ring-nf-ds-surface-card-ring ring-inset outline-hidden placeholder:text-muted-foreground transition-[box-shadow,background-color] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none";

/** Focus is a ring, never a removed outline. It has to be visible to be a state. */
export const FIELD_FOCUS =
  "focus:ring-2 focus:ring-nf-ds-brand focus-visible:ring-2 focus-visible:ring-nf-ds-brand";

/**
 * Invalid is driven by `aria-invalid`, so the look and the announced state
 * cannot diverge.
 */
export const FIELD_INVALID =
  "aria-[invalid=true]:ring-2 aria-[invalid=true]:ring-nf-ds-text-error";

/** A single-line field's geometry: the control height and radius, and the body role for the value it holds. */
export const FIELD_CONTROL =
  "h-nf-ds-control rounded-nf-ds-control px-nf-ds-4 text-nf-ds-body";

/**
 * A multi-line field's geometry. Not the control radius: a pill radius
 * clamps to half the shorter side, so a tall box becomes a lozenge.
 */
export const FIELD_MULTILINE =
  "rounded-nf-ds-inset px-nf-ds-4 py-nf-ds-3 text-nf-ds-body";

/**
 * The pane. A card, a list container, anything holding content on the page.
 *
 * Light is a fill and a soft shadow; dark is a translucent wash behind a blur,
 * which is why the blur is a class rather than a token — it is a mechanism,
 * not a value. Reduced transparency drops back to the solid fill in both.
 */
const PANE_RADIUS = "rounded-nf-ds-card";
const PANE_LIGHT = "bg-nf-ds-surface-card shadow-nf-ds-pane";
const PANE_DARK = [
  // 2.5%, down from 5.5%. Over `#0e0e10` that is roughly `#141416` against
  // the ground's `#0d0d0f` — a step small enough that the pane reads as the
  // same material lifted rather than as a lighter panel laid on top.
  // Definition comes from the highlight and the shadow, which is what dark
  // mode has instead of the fill contrast a light theme can spend.
  "dark:supports-[backdrop-filter]:bg-white/[0.025]",
  "dark:supports-[backdrop-filter]:backdrop-blur-xl",
  "dark:supports-[backdrop-filter]:backdrop-saturate-150",
  "dark:[@media(prefers-reduced-transparency:reduce)]:bg-nf-ds-surface-card",
  "dark:[@media(prefers-reduced-transparency:reduce)]:backdrop-filter-none",
].join(" ");

/**
 * A card that lets an open popover out.
 *
 * In dark the pane carries `backdrop-filter`, which makes it a stacking
 * context — so a popover inside it is trapped there. Lifting the card while it
 * holds one, and again when focus is inside it, is what puts the popover over
 * its neighbours rather than behind them.
 */
const LIFT_FOR_POPOVER =
  "relative has-[[data-popover-open]]:z-50 has-[[data-popover-open]]:focus-within:z-[60]";

export const SURFACE = `${PANE_RADIUS} ${LIFT_FOR_POPOVER} ${PANE_LIGHT} ${PANE_DARK}`;
